import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, ButtonBase, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { Close, ReceiptLong } from '@mui/icons-material';
import {
  billsService, type BillCategoryLine, type BillTransactionsResponse, type BillTxn,
} from '../../services/bills-service';
import { baseMoney } from '../../stores/currency-store';

/** The bill a row was clicked on. Enough to title the dialog before data loads. */
export interface BillDrillTarget {
  id: string;
  payeeName: string;
}

interface Props {
  target: BillDrillTarget | null;
  onClose: () => void;
}

const fmt = (n: string | number | null | undefined) => baseMoney(n, 2);
const num = (s: string | null | undefined) => {
  const n = parseFloat(s ?? '0');
  return Number.isFinite(n) ? n : 0;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Oct 6, 2026" from an ISO date, read as a calendar date (no timezone shift). */
export function calendarDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!m || !d) return iso;
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

type SortKey = 'date' | 'amount';

/** A category's key for filtering: its id, or its name when it has none. */
const catKey = (c: { category: string; category_id: string | null }) => c.category_id ?? `name:${c.category}`;

/**
 * Every transaction behind one bill: what the statement is made of by
 * category, the charges themselves, and the payments and credits kept apart,
 * reconciled to the statement amount.
 *
 * Read-only. Clicking a category filters the list; clicking it again clears.
 */
export const BillTransactionsDialog: React.FC<Props> = ({ target, onClose }) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState<BillTransactionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>('date');

  const load = useCallback(async (id: string) => {
    setLoading(true);
    setFailed(false);
    setData(null);
    try {
      setData(await billsService.transactions(id));
    } catch {
      // The raw error never reaches the user; the copy below says what to do.
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const targetId = target?.id;
  useEffect(() => {
    setFilter(null);
    setSort('date');
    if (targetId) load(targetId);
    else setData(null);
  }, [targetId, load]);

  const shown = useMemo(() => {
    const list = (data?.transactions ?? []).filter((t) => !filter || catKey(t) === filter);
    return [...list].sort((a, b) => (sort === 'amount'
      ? num(b.amount) - num(a.amount)
      : b.date.localeCompare(a.date)));
  }, [data, filter, sort]);

  const filterName = filter && data?.categories.find((c) => catKey(c) === filter)?.category;
  const account = data?.account
    ? `${data.account.name}${data.account.last4 ? ` ••${data.account.last4}` : ''}`
    : null;
  const isEmpty = !!data && data.transactions.length === 0 && data.payments_credits.length === 0;

  return (
    <Dialog open={!!target} onClose={onClose} fullWidth maxWidth="md" fullScreen={phone}
      aria-labelledby="bill-txn-title">
      <DialogTitle id="bill-txn-title" sx={{ pr: 6 }}>
        {data?.payee ?? target?.payeeName}
        {account && (
          <Typography variant="body2" component="span" sx={{ display: 'block', color: 'var(--text-secondary)' }}>
            {account}
          </Typography>
        )}
        <IconButton aria-label="Close" onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'var(--text-secondary)' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 6, justifyContent: 'center' }}>
            <CircularProgress size={22} />
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>Loading transactions…</Typography>
          </Box>
        )}

        {failed && target && (
          <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => load(target.id)}>Try again</Button>}>
            We couldn't load the transactions for this bill. Please try again in a moment.
          </Alert>
        )}

        {data && (
          <>
            <Box sx={{ display: 'grid', gap: 1.5, mb: 2,
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' } }}>
              <Stat label="Statement period"
                value={`${calendarDate(data.window.from)} – ${calendarDate(data.window.to)}`} />
              <Stat label="Statement amount" value={data.statement_amount != null ? fmt(data.statement_amount) : '—'} />
              <Stat label="Charges" value={fmt(data.totals.charges)} />
              <Stat label="Credits" value={fmt(data.totals.credits)} />
            </Box>

            {isEmpty ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <ReceiptLong sx={{ color: 'var(--text-faint)' }} />
                <Typography variant="body2" sx={{ mt: 1 }}>No transactions found for this period</Typography>
                {data.reason && (
                  <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{data.reason}</Typography>
                )}
              </Box>
            ) : (
              <>
                {data.categories.length > 0 && (
                  <CategoryBreakdown categories={data.categories} active={filter}
                    onToggle={(k) => setFilter((f) => (f === k ? null : k))} />
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2.5, mb: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }}>
                    {data.source === 'payment' ? 'Payment' : 'Transactions'}
                    {filterName ? ` · ${filterName}` : ''}
                    <Typography component="span" variant="caption" sx={{ ml: 1, color: 'var(--text-secondary)' }}>
                      {shown.length}
                    </Typography>
                  </Typography>
                  {filter && (
                    <Button size="small" onClick={() => setFilter(null)}>Show all</Button>
                  )}
                  <ToggleButtonGroup size="small" exclusive value={sort} aria-label="Sort transactions"
                    onChange={(_, v: SortKey | null) => v && setSort(v)}>
                    <ToggleButton value="date">Newest</ToggleButton>
                    <ToggleButton value="amount">Largest</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                {shown.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'var(--text-secondary)', py: 2 }}>
                    No charges in this period.
                  </Typography>
                ) : (
                  <TxnList txns={shown} label="Transactions" />
                )}

                {data.payments_credits.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 0.5 }}>Payments &amp; credits</Typography>
                    <TxnList txns={data.payments_credits} label="Payments and credits" credit />
                  </>
                )}
              </>
            )}

            <Reconciliation data={data} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block' }}>{label}</Typography>
    <Typography variant="body2" className="fw-num" sx={{ fontWeight: 600 }}>{value}</Typography>
  </Box>
);

/** Charges by category as horizontal bars; each bar filters the list. */
const CategoryBreakdown: React.FC<{
  categories: BillCategoryLine[]; active: string | null; onToggle: (key: string) => void;
}> = ({ categories, active, onToggle }) => {
  const max = Math.max(1, ...categories.map((c) => num(c.amount)));
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>By category</Typography>
      <Box component="ul" aria-label="Charges by category" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {categories.map((c) => {
          const key = catKey(c);
          const on = active === key;
          return (
            <Box component="li" key={key}>
              <ButtonBase onClick={() => onToggle(key)} aria-pressed={on}
                aria-label={`${c.category} ${fmt(c.amount)}, ${num(c.percent).toFixed(0)}% of charges. ${on ? 'Clear filter' : 'Show these transactions'}`}
                sx={{ display: 'block', width: '100%', textAlign: 'left', py: 0.6, px: 0.75, borderRadius: 1,
                  background: on ? 'var(--glass-2)' : 'transparent',
                  '&:hover': { background: 'var(--glass-1)' },
                  '&.Mui-focusVisible, &:focus-visible': { outline: '2px solid var(--accent)', outlineOffset: 1 } }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontWeight: on ? 700 : 400,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {c.category}
                    <Typography component="span" variant="caption" sx={{ ml: 0.75, color: 'var(--text-faint)' }}>
                      {c.count}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" className="fw-num" sx={{ color: 'var(--text-secondary)' }}>
                    {num(c.percent).toFixed(0)}%
                  </Typography>
                  <Typography variant="body2" className="fw-num" sx={{ fontWeight: 600, minWidth: 84, textAlign: 'right' }}>
                    {fmt(c.amount)}
                  </Typography>
                </Box>
                <Box sx={{ height: 6, mt: 0.5, borderRadius: 3, background: 'var(--glass-2)' }}>
                  <Box sx={{ height: '100%', borderRadius: 3, width: `${(num(c.amount) / max) * 100}%`,
                    background: 'var(--accent)', opacity: active && !on ? 0.35 : 1 }} />
                </Box>
              </ButtonBase>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/**
 * Rows rather than a table, so the list reads at phone width: description and
 * category on the left, date under them, amount on the right.
 */
const TxnList: React.FC<{ txns: BillTxn[]; label: string; credit?: boolean }> = ({ txns, label, credit }) => (
  <Box component="ul" aria-label={label} sx={{ listStyle: 'none', m: 0, p: 0 }}>
    {txns.map((t) => (
      <Box component="li" key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75,
        borderTop: '1px solid var(--border-soft)' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {t.description}
          </Typography>
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
            {calendarDate(t.date)} · {credit ? (t.kind === 'payment' ? 'Payment' : 'Credit') : t.category}
          </Typography>
        </Box>
        <Typography variant="body2" className="fw-num"
          sx={{ fontWeight: 600, flexShrink: 0, color: credit ? 'var(--inflow-text)' : undefined }}>
          {credit ? `−${fmt(t.amount)}` : fmt(t.amount)}
        </Typography>
      </Box>
    ))}
  </Box>
);

/** "Charges − credits = $X vs statement $Y", and whether it matches. */
const Reconciliation: React.FC<{ data: BillTransactionsResponse }> = ({ data }) => {
  const { totals } = data;
  const diff = totals.difference != null ? num(totals.difference) : null;
  const matched = diff != null && Math.abs(diff) < 0.01;
  return (
    <Box data-testid="bill-reconciliation" sx={{ mt: 2.5, pt: 1.5, borderTop: '1px solid var(--border-soft)' }}>
      <Typography variant="body2" className="fw-num">
        Charges − credits = <b>{fmt(totals.net)}</b>
        {totals.statement_amount != null ? <> vs statement <b>{fmt(totals.statement_amount)}</b></> : ' · no statement amount entered'}
      </Typography>
      {diff != null && (
        <Typography variant="caption" sx={{ color: matched ? 'var(--inflow-text)' : 'var(--warn-text)' }}>
          {matched
            ? 'Reconciled to the statement.'
            : `Off by ${fmt(Math.abs(diff))}. Interest, fees, a carried balance or transactions not imported yet can explain the gap.`}
        </Typography>
      )}
    </Box>
  );
};
